import {
  Radio,
  Briefcase,
  Users,
  MessageCircle,
  Bookmark,
  Rocket,
  Bot,
  Radar,
  Bell,
  Building2,
  Lock,
  LifeBuoy,
  LogOut,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import logo from "@/assets/logo-chillwerk.png";
import { useCompanyPremiumStatus } from "@/hooks/useCompanyPremiumStatus";
import { useCompanySidebarBadges } from "@/hooks/useCompanySidebarBadges";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

interface Item {
  title: string;
  url: string;
  icon: React.ElementType;
  premium?: boolean;
  badgeKey?: "hub" | "messages" | "talent" | "todo" | "ai";
}

const MAIN_ITEMS: Item[] = [
  { title: "Control Room", url: "/bedrijf/control-room", icon: Radio, badgeKey: "todo" },
  { title: "Kandidaten", url: "/bedrijf/matches", icon: Users, badgeKey: "hub" },
  { title: "Berichten", url: "/bedrijf/berichten", icon: MessageCircle, badgeKey: "messages" },
  { title: "Vacatures", url: "/vacatures/beheer", icon: Briefcase },
];

const TALENT_ITEMS: Item[] = [
  { title: "TalentRadar", url: "/kandidaten", icon: Radar, premium: true, badgeKey: "talent" },
  { title: "AI Recruiters", url: "/bedrijf/ai-recruiter", icon: Bot, premium: true, badgeKey: "ai" },
  { title: "Opgeslagen", url: "/bedrijf/opgeslagen", icon: Bookmark },
];

const GROW_ITEMS: Item[] = [
  { title: "Vacature Boost", url: "/bedrijf/vacature-boost", icon: Rocket },
  { title: "Bedrijfsprofiel", url: "/bedrijf/edit", icon: Building2 },
];

const NotificationBadge = ({ count }: { count: number }) => (
  <span className="ml-auto shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-yellow text-yellow-foreground text-[11px] font-black flex items-center justify-center shadow-sm animate-scale-in">
    {count > 99 ? "99+" : count}
  </span>
);

export function CompanySidebar() {
  const { open } = useSidebar();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { data: isPremium } = useCompanyPremiumStatus();
  const badges = useCompanySidebarBadges();


  const renderGroup = (label: string, items: Item[]) => (
    <SidebarGroup key={label}>
      {open && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const locked = !!item.premium && !isPremium;
            const count = !locked && item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={count > 0 ? `${item.title} (${count} nieuw)` : item.title}>
                  <NavLink
                    to={item.url}
                    end
                    className="hover:bg-muted/50 relative"
                    activeClassName="bg-cyan-light text-cyan-dark font-black"
                  >
                    <span className="relative shrink-0">
                      <item.icon className="w-4 h-4" />
                      {count > 0 && !open && (
                        <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full bg-[#FFE600] ring-2 ring-white" />
                      )}
                    </span>
                    {open && (
                      <span className="flex-1 flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{item.title}</span>
                        {locked && <Lock className="w-3 h-3 shrink-0 opacity-50" />}
                        {count > 0 && <NotificationBadge count={count} />}
                      </span>
                    )}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar className={open ? "w-60" : "w-14"} collapsible="icon">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <img src={logo} alt="ChillWerk.nl" width={32} height={32} className="w-8 h-8 rounded-full object-contain shrink-0" />
          {open && <span className="font-heading font-black text-lg">Werkgever</span>}
        </div>
      </div>

      <SidebarContent>
        {renderGroup("Werk", MAIN_ITEMS)}
        {renderGroup("Talent", TALENT_ITEMS)}
        {renderGroup("Groei", GROW_ITEMS)}
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Support">
              <NavLink
                to="/support"
                end
                className="hover:bg-muted/50"
                activeClassName="bg-cyan-light text-cyan-dark font-black"
              >
                <LifeBuoy className="w-4 h-4 shrink-0" />
                {open && <span className="truncate">Support</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Uitloggen"
              onClick={async () => {
                await signOut();
                navigate("/");
              }}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              {open && <span className="truncate">Uitloggen</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
